import { computed, Ref, ref } from "vue";
import { ResponsiveUtils } from "../responsive-utils";

export type BreakpointType = keyof typeof ResponsiveUtils.breakpoints;
type ReactiveBreakpoint = {
    key: BreakpointType;
    minSize: (typeof ResponsiveUtils.breakpoints)[BreakpointType];
    isInUse: boolean;
    value: Ref<boolean>;
};

let hasInitialized = false;
let reactiveBreakpoints: ReactiveBreakpoint[] = null;

/**
 * Returns ref with boolean value, which means, current breakpoint is same or bigger
 * eg. media min-width: x
 * since its breakpoint up, it doesnt make sense for 'xs' which would be always true, so its omited
 * @param breakpoints breakpoint name eg. 'xs2' or 'xl'
 * @returns boolean
 */
export function useBreakpoints(breakpoints: BreakpointType): Ref<boolean>;

/**
 * Returns object, with keys same as parameter value, their value is ref with boolean value, which means, current breakpoint is same or bigger
 * eg. media min-width: x
 * Example of return value: { sm: ref(true), md: ref(false) }
 * Returns only value passed in parametrs,
 * since its breakpoint up, it doesnt make sense for 'xs' which would be always true, so its omited
 * @param breakpoints all breakpoint names eg. 'xs2' or 'xl'
 */
export function useBreakpoints(...breakpoints: BreakpointType[]): { [key in BreakpointType]: Ref<boolean> };

export function useBreakpoints(...breakpoints: BreakpointType[]) {
    if (breakpoints?.length == 0) return;

    initialize();

    if (breakpoints.length > 1) {
        return breakpoints.reduce((acc, x) => {
            acc[x] = reactiveBreakpoint(x);
            return acc;
        }, {});
    } else {
        return reactiveBreakpoint(breakpoints[0]);
    }
}

/**
 * Returns a computedRef with the name of the highest active breakpoint
 *
 * @param selectedBreakpoints optional array of breakpoints to consider, if not provided all breakpoints are considered, xs is always included
 */
export function useCurrentBreakpoint(selectedBreakpoints?: BreakpointType[]) {
    if (!selectedBreakpoints || selectedBreakpoints.length === 0) {
        selectedBreakpoints = Object.keys(ResponsiveUtils.breakpoints) as BreakpointType[];
    }

    const sortedBreakpoints = selectedBreakpoints.sort(
        (a, b) => ResponsiveUtils.breakpoints[a] - ResponsiveUtils.breakpoints[b]
    );

    initialize();

    const activeBps = sortedBreakpoints.map((x) => ({ key: x, value: reactiveBreakpoint(x) }));
    const highestActiveBp = computed(() => {
        return activeBps.reduce((acc, x) => (x.value.value ? x.key : acc), "xs" as BreakpointType);
    });

    return highestActiveBp;
}

function initialize() {
    if (hasInitialized) return;
    reactiveBreakpoints = Object.keys(ResponsiveUtils.breakpoints).map((bpKey) => ({
        isInUse: bpKey === "xs",
        key: bpKey as BreakpointType,
        minSize: ResponsiveUtils.breakpoints[bpKey] as (typeof ResponsiveUtils.breakpoints)[BreakpointType],
        value: ref(bpKey === "xs"),
    }));

    hasInitialized = true;
}

function reactiveBreakpoint(breakpoint: BreakpointType) {
    const brkpnt = reactiveBreakpoints.find((x) => x.key === breakpoint);
    if (!brkpnt.isInUse) {
        brkpnt.isInUse = true;
        const media = window.matchMedia(`(min-width: ${brkpnt.minSize}px)`);
        brkpnt.value.value = media.matches;
        media.onchange = (e) => {
            brkpnt.value.value = e.matches;
        };
    }
    return brkpnt.value;
}
